import Link from 'next/link'
import { Image } from '@educator/components/Image'
import { Container } from '@educator/components/ProgramSections/Container'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '@educator/components/ProgramSections/i18n'
import { MobileMenuButton } from '@educator/components/TopNavigation/MobileMenu'
import topNavClasses from '@educator/components/TopNavigation/TopNavigation.module.scss'
import { getBasePath } from '@educator/config/basePath'
import { useEnrollmentCtaConfig } from '@educator/hooks/useEnrollmentCtaConfig'
import type { CtaItem } from '@educator/hooks/useEnrollmentCtaConfig'
import { TrackClick, useFireEvent } from '@educator/tracking/educatorTracking'
import { smoothScrollToElement } from '@educator/utils/smoothScrollTo'
import { cn } from '@mc/educator-design-system'
import { useTranslation } from '@mc/i18n'
import { PROGRAM_NAV_ITEMS } from '../navItems'

/** Offset the smooth-scroll leaves above the target section. */
const SCROLL_OFFSET = 100

/** Nav link / text-CTA style — Figma caps; 10px mobile, 12px tablet, 14px desktop. */
const NAV_TEXT =
  'whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.6px] text-white md:text-xs lg:text-sm'

/** 36px-tall centered nav control — the box every nav button shares. */
const NAV_CONTROL = 'cursor-pointer flex h-9 items-center justify-center'
const NAV_CONTROL_OUTLINED = cn(
  NAV_CONTROL,
  'border border-edu-stroke-strong px-3',
  NAV_TEXT,
)

/** Purpose-built to Figma — not the shared NavBar. In-flow (non-sticky). */
export const ProgramNavBar = () => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)
  const fireEvent = useFireEvent()
  const { primaryCta, secondaryCta, isPrimaryLogIn } = useEnrollmentCtaConfig()
  const basePath = getBasePath()

  const handleNavClick = (targetId: string) => {
    // No-op if the target section isn't mounted.
    const target = document.getElementById(targetId)
    if (!target) return
    smoothScrollToElement(target, SCROLL_OFFSET)
    // Move keyboard/SR focus into the jumped-to section (native anchor parity).
    target.setAttribute('tabindex', '-1')
    target.focus({ preventScroll: true })
  }

  const fireCtaClick = (cta: CtaItem, isLogIn: boolean) => {
    if (!isLogIn) {
      fireEvent('educatorPurchaseClick', {
        cta_location: 'top navigation',
        cta: cta.label,
      })
    }
    fireEvent('educatorSignClick', {
      action: cta.label.toLowerCase(),
      location: 'top navigation',
    })
  }

  const renderLogo = (src: string, width: number, height: number) => (
    <TrackClick
      event='educatorSignClick'
      action='masterclass'
      location='top navigation'
    >
      <button
        type='button'
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label={t('nav.logoAlt')}
        className='cursor-pointer flex shrink-0 items-center'
      >
        <Image
          src={`${basePath}/images/${src}`}
          alt={t('nav.logoAlt')}
          width={width}
          height={height}
        />
      </button>
    </TrackClick>
  )

  const primaryLink = (className: string) => (
    <Link
      href={primaryCta.href}
      onClick={() => fireCtaClick(primaryCta, isPrimaryLogIn)}
      className={className}
    >
      {primaryCta.label}
    </Link>
  )

  const secondaryLink = (className: string) => (
    <Link
      href={secondaryCta.href}
      onClick={() => fireCtaClick(secondaryCta, !isPrimaryLogIn)}
      className={className}
    >
      {secondaryCta.label}
    </Link>
  )

  return (
    <>
      {/* Mobile */}
      <header className='w-full md:hidden'>
        <nav>
          <div className='flex items-center justify-between gap-4 px-5 py-3'>
            {renderLogo('executive-logo.svg', 102, 30)}
            <div className='flex items-center gap-2'>
              {/* Below sm only the secondary CTA fits beside the logo; the hero
                and the sticky bar carry the primary one. */}
              {secondaryLink(NAV_CONTROL_OUTLINED)}
              {primaryLink(cn(NAV_CONTROL_OUTLINED, 'max-sm:hidden'))}
              <div
                className={`${topNavClasses.wrapper} ${topNavClasses.burgerWhite}`}
              >
                {/* Bump the shared burger icon from sm to md here (Figma) without
                  modifying the shared MobileMenuButton — keeps the experiment
                  self-contained. */}
                <div
                  className={cn(
                    NAV_CONTROL,
                    'w-11 border border-edu-stroke-strong [&_.mc-icon]:size-5',
                  )}
                >
                  <MobileMenuButton />
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* Desktop — gaps around the bar are owned by the page. */}
      <header className='hidden w-full md:block'>
        <nav>
          {/* `bleed` so the explicit insets win over Container's own. */}
          <Container bleed className='px-6 md:px-10 lg:px-25'>
            {/* 3-column grid keeps the section links centered without absolute
              positioning, so they can never overlap the logo/CTAs at narrow
              desktop widths. */}
            <div className='grid h-[42px] grid-cols-[1fr_auto_1fr] items-center gap-x-6 lg:gap-x-8'>
              {/* Tablet uses the stacked logo; the one-line lockup can't share the row below lg. */}
              <div className='justify-self-start'>
                <div className='lg:hidden'>
                  {renderLogo('executive-logo.svg', 108, 32)}
                </div>
                <div className='hidden lg:block'>
                  {renderLogo('executive-logo-one-line.svg', 287, 24)}
                </div>
              </div>
              <div className='flex items-center gap-4 justify-self-center lg:gap-6'>
                {PROGRAM_NAV_ITEMS.map((item) => (
                  <TrackClick
                    key={item.id}
                    event='educatorSignClick'
                    action={item.id}
                    location='top navigation'
                  >
                    <button
                      type='button'
                      onClick={() => handleNavClick(item.targetId)}
                      className={`cursor-pointer ${NAV_TEXT}`}
                    >
                      {t(item.labelKey)}
                    </button>
                  </TrackClick>
                ))}
              </div>
              <div className='flex items-center gap-3 justify-self-end lg:gap-4'>
                {secondaryLink(`cursor-pointer ${NAV_TEXT}`)}
                {primaryLink(
                  cn(
                    NAV_CONTROL,
                    'bg-edu-accent-indigo-banner px-3 lg:px-4',
                    NAV_TEXT,
                  ),
                )}
              </div>
            </div>
          </Container>
        </nav>
      </header>
    </>
  )
}
